import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Paper, Avatar,
  Typography, Box, TableSortLabel, Button, Toolbar, Tooltip
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteAttachments } from '../../../../services/attachments';
import { updateMessages, updateGroupMessages } from '../../../../services/conversations';

const FileListTable = ({ files, setMedia, isGroupChat, deleteMessageFromState }) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [hoveredRow, setHoveredRow] = useState(null);
  const [checkedRows, setCheckedRows] = useState([]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleCheckToggle = (id) => {
    setCheckedRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleCheckAll = () => {
    if (checkedRows.length === files.length) {
      setCheckedRows([]);
    } else {
      setCheckedRows(files.map(file => file?._id));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete selected files?')) return;

    const selectedFilesInfo = files
      .filter(file => checkedRows.includes(file._id))
      .map(file => ({
        filePath: file.filePath,
        id: file._id,
        type: file.fileType.split("/")?.[0],
        conversation: file.conversation
      }));

    const type = isGroupChat ? "groupMessage" : "message"
    try {
      await Promise.all(
        selectedFilesInfo.map(async (file) => {
          await deleteAttachments(file.filePath, file?.conversation?._id, type);
          deleteMessageFromState(file?.conversation?._id);
        })
      );

      // Filter out the deleted files and update media
      const updatedFiles = files.filter(
        file => !checkedRows.includes(file._id)
      );
      setMedia(updatedFiles);

      // Clear selected rows
      setCheckedRows([]);
    } catch (error) {
      console.error('Error deleting files:', error);
    }
  };

  const sortedFiles = [...files].sort((a, b) => {
    if (orderBy === 'date') {
      return order === 'asc'
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt);
    }
    return order === 'asc'
      ? a[orderBy]?.localeCompare(b[orderBy])
      : b[orderBy]?.localeCompare(a[orderBy]);
  });

  return (
    <Box position="relative">
      {/* Delete Button when files selected */}
      {checkedRows.length > 0 && (
        <Toolbar
          sx={{
            position: 'absolute',
            top: -15,
            right: 0,
            zIndex: 1,
            backgroundColor: 'transparent',
            p: 0
          }}
        >
          <Tooltip title="Delete selected files">
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Tooltip>
        </Toolbar>
      )}

      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" onChange={handleCheckAll} />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  File
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleSort('date')}
                >
                  Shared on
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'author'}
                  direction={orderBy === 'author' ? order : 'asc'}
                  onClick={() => handleSort('author')}
                >
                  Sent by
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedFiles.map((file, index) => {
              const isHovered = hoveredRow === index;
              const isChecked = checkedRows.includes(file?._id);
              return (
                <TableRow
                  key={file._id || index}
                  hover
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => handleCheckToggle(file._id)}
                      sx={{
                        visibility: isHovered || isChecked ? 'visible' : 'hidden',
                        transition: 'visibility 0.2s ease-in-out'
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        variant="rounded"
                        sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}
                      >
                        <InsertDriveFileIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" fontSize={13}>
                        {file.fileName}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontSize={13}>
                      {new Date(file.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontSize={13}>
                      {file.sentBy}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FileListTable;
